import { useEffect, useState, useRef } from 'react';
import { Row, Col, Card, Statistic, Skeleton, message, Table, Tag, Empty, Badge } from 'antd';
import {
  DesktopOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  VideoCameraOutlined,
  WifiOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { reportAPI, screenAPI, videoAPI } from '../services/api';
import { themeColors } from '../config/theme';

const CHART_COLORS = ['#F97316', '#22C55E', '#3B82F6', '#EAB308', '#EF4444', '#A855F7', '#06B6D4'];

function ChartCard({ title, data, emptyMessage }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card
      title={title}
      style={{
        backgroundColor: themeColors.bgElevated,
        borderColor: themeColors.border,
        height: '100%',
      }}
    >
      {total === 0 ? (
        <Empty description={emptyMessage} style={{ padding: '40px 0' }} />
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={90}
              paddingAngle={2}
              label={({ name, value }) => `${name}: ${value}`}
            >
              {data.map((entry, index) => (
                <Cell key={entry.name} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [screens, setScreens] = useState([]);
  const [expiringCount, setExpiringCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchStats();
    fetchActivity();

    pollRef.current = setInterval(fetchActivity, 10000);
    return () => clearInterval(pollRef.current);
  }, []);

  const fetchActivity = async () => {
    try {
      const response = await reportAPI.recentActivity({ limit: 10 });
      setActivity(response.data.data);
    } catch (error) {
      console.error('Failed to load recent activity');
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [dashboardRes, screenRes, videoRes, screenListRes, expiringRes] = await Promise.all([
        reportAPI.dashboard(),
        screenAPI.stats(),
        videoAPI.stats(),
        screenAPI.list({ limit: 100 }),
        reportAPI.videoSchedule({ expiringSoon: true }),
      ]);

      setStats({
        ...dashboardRes.data.data,
        screenStats: screenRes.data.data,
        videoStats: videoRes.data.data,
      });
      setScreens(screenListRes.data.data.data);

      const uniqueExpiring = new Set(
        expiringRes.data.data.map((row) => `${row.schedule_id}-${row.video_id}`)
      );
      setExpiringCount(uniqueExpiring.size);
    } catch (error) {
      message.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Row gutter={[16, 16]}>
        {[...Array(4)].map((_, i) => (
          <Col xs={24} sm={12} lg={6} key={i}>
            <Skeleton active />
          </Col>
        ))}
      </Row>
    );
  }

  const statCards = [
    {
      title: 'Online Screens',
      value: stats?.onlineScreens || 0,
      total: stats?.totalScreens || 0,
      icon: <DesktopOutlined />,
      gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
    },
    {
      title: 'Videos Approved',
      value: stats?.videoStats?.approved || 0,
      icon: <CheckCircleOutlined />,
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
    },
    {
      title: 'Pending Videos',
      value: stats?.videoStats?.pending || 0,
      icon: <ClockCircleOutlined />,
      gradient: 'linear-gradient(135deg, #EAB308 0%, #CA8A04 100%)',
    },
    {
      title: 'Total Playbacks (24h)',
      value: stats?.totalPlaybacks || 0,
      icon: <VideoCameraOutlined />,
      gradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
    },
  ];

  const screenChartData = [
    { name: 'Online', value: stats?.screenStats?.online || 0, color: '#22C55E' },
    { name: 'Offline', value: stats?.screenStats?.offline || 0, color: '#EF4444' },
  ];

  const videoChartData = [
    { name: 'Approved', value: stats?.videoStats?.approved || 0, color: '#22C55E' },
    { name: 'Pending', value: stats?.videoStats?.pending || 0, color: '#EAB308' },
    { name: 'Rejected', value: stats?.videoStats?.rejected || 0, color: '#EF4444' },
  ];

  const expiringChartData = [
    { name: 'Expiring (3 days)', value: expiringCount, color: '#EF4444' },
    {
      name: 'Healthy',
      value: Math.max((stats?.videoStats?.approved || 0) - expiringCount, 0),
      color: '#22C55E',
    },
  ];

  const activityColumns = [
    {
      title: 'Screen',
      key: 'screen',
      render: (_, record) => record.screen_name || record.screen_code,
    },
    {
      title: 'Video',
      dataIndex: 'video_title',
      key: 'video_title',
      render: (value) => value || '-',
    },
    {
      title: 'Started At',
      dataIndex: 'started_at',
      key: 'started_at',
      render: (value) => new Date(value).toLocaleString(),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      render: (value) => (value ? `${value}s` : '-'),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) =>
        record.ended_at ? (
          <Tag color="default">Finished</Tag>
        ) : (
          <Tag color="success">Playing</Tag>
        ),
    },
  ];

  const screenColumns = [
    {
      title: 'Screen',
      dataIndex: 'screen_name',
      key: 'screen_name',
    },
    {
      title: 'Code',
      dataIndex: 'screen_code',
      key: 'screen_code',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (value) => value || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => (
        <Tag
          icon={value === 'online' ? <WifiOutlined /> : <CloseCircleOutlined />}
          color={value === 'online' ? 'success' : 'error'}
        >
          {value?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Last Seen',
      dataIndex: 'last_seen',
      key: 'last_seen',
      render: (value) => (value ? new Date(value).toLocaleString() : 'Never'),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '30px' }}>
        {statCards.map((card, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card
              style={{
                background: card.gradient,
                border: 'none',
                color: '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', marginBottom: '8px' }}>
                    {card.title}
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#fff' }}>
                    {card.value}
                  </div>
                  {card.total !== undefined && (
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', marginTop: '4px' }}>
                      of {card.total} total
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '36px', color: 'rgba(255,255,255,0.9)' }}>{card.icon}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24} lg={8}>
          <ChartCard title="Screens by Status" data={screenChartData} emptyMessage="No screens yet" />
        </Col>
        <Col xs={24} lg={8}>
          <ChartCard title="Videos by Status" data={videoChartData} emptyMessage="No videos yet" />
        </Col>
        <Col xs={24} lg={8}>
          <ChartCard
            title={
              <span>
                <WarningOutlined style={{ color: themeColors.error, marginRight: 8 }} />
                Videos Expiring Soon
              </span>
            }
            data={expiringChartData}
            emptyMessage="Nothing expiring soon"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24} lg={12}>
          <Card
            title="Offline Screens"
            style={{
              backgroundColor: themeColors.bgElevated,
              borderColor: themeColors.border,
            }}
          >
            <Statistic
              value={stats?.offlineScreens || 0}
              suffix={`/ ${stats?.totalScreens || 0}`}
              valueStyle={{ color: themeColors.error }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title="Unique Screens (24h)"
            style={{
              backgroundColor: themeColors.bgElevated,
              borderColor: themeColors.border,
            }}
          >
            <Statistic
              value={stats?.uniqueScreens || 0}
              valueStyle={{ color: themeColors.primary }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <span>
            <Badge status="processing" style={{ marginRight: 8 }} />
            Realtime Activity
          </span>
        }
        style={{
          backgroundColor: themeColors.bgElevated,
          borderColor: themeColors.border,
          marginBottom: '16px',
        }}
      >
        <Table
          rowKey="id"
          columns={activityColumns}
          dataSource={activity}
          pagination={{ pageSize: 5 }}
          locale={{ emptyText: 'No playback activity recorded yet' }}
        />
      </Card>

      <Card
        title="Screens Overview"
        style={{
          backgroundColor: themeColors.bgElevated,
          borderColor: themeColors.border,
        }}
      >
        <Table
          rowKey="id"
          columns={screenColumns}
          dataSource={screens}
          pagination={{ pageSize: 5 }}
        />
      </Card>
    </div>
  );
}
